import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";

/** Glasronde 0.9.244 B (Frank, 4 nov): regels die dubbel lijken, als paar; Frank kiest welke blijft.
 *
 *  Er gaat niets automatisch weg. "Houd deze" laat de andere regel naar de prullenbak gaan (30 dagen, terug te zetten).
 *  `regels` zijn de regels van de pagina, voor de tekst; `teller` laat opnieuw lezen na een wijziging; `onGekozen` laat
 *  de pagina haar lijst opnieuw lezen.
 */
export default function LijktDubbel({ regels, teller, onGekozen }) {
  const { t } = useTaal();
  const [paren, setParen] = useState([]);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let weg = false;
    axios.get(`${API}/memory/lijkt-dubbel`)
      .then((r) => { if (!weg) setParen(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!weg) setParen([]); });
    return () => { weg = true; };
  }, [teller]);

  const tekst = Object.fromEntries((regels || []).map((r) => [r.id, r.text]));
  const zichtbaar = paren.filter((p) => tekst[p.a] && tekst[p.b]);
  if (!zichtbaar.length) return null;

  const kies = async (blijft, weg) => {
    setBezig(true);
    try {
      await axios.post(`${API}/memory/lijkt-dubbel/kies`, { blijft, weg });
      toast.success(t("De andere regel staat 30 dagen in de prullenbak."));
      setParen((oud) => oud.filter((p) => p.a !== weg && p.b !== weg));
      if (onGekozen) onGekozen();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <section className="border rounded-lg p-3 space-y-3 mb-4" data-testid="lijkt-dubbel">
      <div>
        <h3 className="text-sm font-medium">{t("Lijkt dubbel")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("Deze regels lijken hetzelfde te zeggen. Kies welke blijft; de andere gaat naar de prullenbak en kan terug.")}
        </p>
      </div>
      {zichtbaar.map((p) => (
        <div key={`${p.a}-${p.b}`} className="grid gap-2 sm:grid-cols-2" data-testid="dubbel-paar">
          {[[p.a, p.b], [p.b, p.a]].map(([blijft, weg]) => (
            <div key={blijft} className="border rounded-md p-2 text-sm flex flex-col gap-2">
              <span className="break-words">{tekst[blijft]}</span>
              <Button size="sm" variant="outline" className="self-start" disabled={bezig}
                      onClick={() => kies(blijft, weg)} data-testid={`houd-${blijft}`}>
                {t("Houd deze")}
              </Button>
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}
